import { usePosShopStore } from "~/stores/pos.shop";
import { OrderItemHelper } from "../OrderItemHelper";
import { getPrintPayloadStrategy, getPrintPayloadStrategyByKey } from "./payload";
import { displayDiscountBillText } from "./payload/derive/labels";
import { isMultiplePayment } from "./payload/derive/customSections";
import { getPrinterAppVersion } from "~/utils/printerVersion";

/**
 * Builds the payload posted to the local print service.
 *
 * The shape differs by printer-interface version, so the work lives in
 * `./payload` behind a strategy: >= 3.20 takes its totals from Summary1..4,
 * older installs from the numeric fields. This class stays a thin adapter
 * because `ThermalPrintReceipt`, `reprintReceipt`, `Orders.vue` and
 * `Receipts.vue` all construct it.
 *
 * The presentation blocks (Summary1..4, CustomHeaders, CustomFooters) are always
 * DERIVED here and never read off the record — a stored block on an old receipt
 * was built by whatever version shipped that day, so honouring it would make two
 * receipts from the same shop print differently.
 */
export default class PrintReceiptNiceloopV1Builder extends OrderItemHelper {
  private receipt: any = null;

  constructor(_receipt: any) {
    super();
    const shopStore = usePosShopStore();

    this.receipt = {
      ..._receipt,
      ShopId: shopStore.getStoreId,
    };
  }

  build(options: any = {}) {
    // `isPrinterInterfaceV3` is what every call site already passes. Honour it
    // when present so behaviour is unchanged; otherwise resolve from the
    // detected AppVersion.
    const strategy =
      options.isPrinterInterfaceV3 === undefined
        ? getPrintPayloadStrategy(getPrinterAppVersion())
        : getPrintPayloadStrategyByKey(options.isPrinterInterfaceV3 ? "v3" : "legacy");

    return strategy.build(this.receipt, {
      isPreview: options.isPreview,
      isCopy: options.isCopy,
      isPrintAll: options.isPrintAll,
      enableCustomerCount: options.enableCustomerCount,
      printMode: options.printMode,
    });
  }

  checkMultiplePayment(payments: any) {
    return isMultiplePayment(payments);
  }

  displayPaymentTypes(payments: any) {
    if (!payments) return "";

    if (typeof payments === "string") {
      try {
        payments = JSON.parse(payments);
      } catch {
        // Not JSON — a plain payment name such as "cash".
        return payments;
      }
    }

    if (!Array.isArray(payments)) return "";

    return payments
      .map((p: any) => `${p.name || p.type}: ${p.amount}`)
      .join(", ");
  }

  displayDiscountBillText(mode = 0, value = 0) {
    return displayDiscountBillText(mode, value);
  }
}
